import { Divider } from "@mui/material";
import { AlarmIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";

import { Avatar } from "@/components/Avatar/Avatar";
import { Button } from "@/components/Button/Button";
import { Chip } from "@/components/Chip/Chip";
import { CircularProgress } from "@/components/CircularProgress/CircularProgress";
import { ReservationDocumentList } from "@/features/bookings/components/ReservationDocumentList";
import { type SingleFightDetailsData } from "@/features/flights/contexts/SingleFlightDetailsContext";
import type { FlightType } from "@/features/flights/types/types";
import { useOrder } from "@/hooks/useOrder";
import type { FindFlightOffersFlightStopV3 } from "@/types/codegen";
import { type Traveller } from "@/types/types";
import { parseDuration } from "@/utils/duration";

import { BaggageAllowance } from "../BaggageAllowance";
import FlightArrow from "../FlightArrow";
import { FlightInfo } from "../flights/FlightInfo";

import { TravellersListSummary } from "./TravellersListSummary";

function FlightBookSuccess({
  departureStops,
  selectedTravellers,
  departureFlight,
  returnStops,
  returnFlight,
  singleFlightDetails,
  orderId,
}: {
  departureStops: FindFlightOffersFlightStopV3[];
  selectedTravellers: Traveller[];
  returnStops: FindFlightOffersFlightStopV3[];
  departureFlight?: FlightType;
  returnFlight?: FlightType;
  singleFlightDetails?: SingleFightDetailsData;
  orderId: string;
}) {
  const t = useTranslations();
  const navigate = useNavigate();

  const { data: orderData, isLoading: isOrderLoading } = useOrder(orderId);

  const isLoading = isOrderLoading || !orderData?.reservations.length;

  return (
    <div className="flex w-full flex-col items-center gap-4 px-4">
      <h5 className="mt-6 mb-10 text-xl font-semibold text-black">
        {t("flights.flightBooked")}
      </h5>
      <div className="section-card flex w-full items-center justify-between gap-4">
        <span className="section-title mb-0">{t("flights.ticketNumber")}</span>
        <h3 className="text-base font-semibold text-text-title">
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            orderData.reservations[0].reservationId
          )}
        </h3>
      </div>

      {orderData?.reservations.map((reservation) => (
        <ReservationDocumentList
          key={reservation.id}
          reservationId={reservation.reservationId}
        />
      ))}

      <div className="section-card flex w-full flex-col gap-4">
        {departureFlight && (
          <span className="section-title m-0">{t("flights.departure")}</span>
        )}
        {departureFlight &&
          departureFlight.segments.map((segment, index) => (
            <div
              key={index}
              className="flex w-full flex-col items-center gap-2"
            >
              <div className="flex w-full justify-between">
                <div className="flex items-center gap-2">
                  <Avatar
                    alt={t("flights.airlineLogoAlt", {
                      airline: segment.marketingCarrier.airline.name,
                    })}
                    size="small"
                    src={segment.marketingCarrier.airline.logoUrl}
                    fallback={
                      segment.marketingCarrier.airline.name?.split("")[0]
                    }
                  />
                  <span className="text-xs text-text-primary">
                    {segment.marketingCarrier.airline.name ||
                      t("unknownAirline")}
                  </span>
                </div>
                <span className="text-xs text-text-tertiary">
                  {segment.equipment.name || segment.equipment.code}{" "}
                  {segment.marketingCarrier.flightNumber}
                </span>
              </div>

              <FlightInfo
                origin={{
                  date: segment.departure.date,
                  time: segment.departure.time,
                  code: segment.departure.airport.code,
                }}
                destination={{
                  date: segment.arrival.date,
                  time: segment.arrival.time,
                  code: segment.arrival.airport.code,
                }}
              >
                <FlightArrow overallTime={segment.duration} />
              </FlightInfo>

              {departureStops.length > 0 && departureStops[index]?.stopTime && (
                <Chip
                  icon={<AlarmIcon size={16} />}
                  color="primary"
                  label={t("flights.flightStopWithTimeAndCity", {
                    ...parseDuration(departureStops[index].stopTime),
                    cityName: segment.arrival.city.name,
                    code: segment.arrival.airport.code,
                  })}
                />
              )}
            </div>
          ))}
        {returnFlight && (
          <span className="section-title m-0">
            {t("flights.search.return")}
          </span>
        )}
        {returnFlight &&
          returnFlight.segments.map((segment, index) => (
            <div
              key={index}
              className="flex w-full flex-col items-center gap-4"
            >
              <div className="flex w-full justify-between">
                <div className="flex items-center gap-2">
                  <Avatar
                    alt={t("flights.airlineLogoAlt", {
                      airline: segment.marketingCarrier.airline.name,
                    })}
                    size="small"
                    src={segment.marketingCarrier.airline.logoUrl}
                    fallback={
                      segment.marketingCarrier.airline.name?.split("")[0]
                    }
                  />
                  <span className="text-xs text-text-primary">
                    {segment.marketingCarrier.airline.name ||
                      t("unknownAirline")}
                  </span>
                </div>
                <span className="text-xs text-text-tertiary">
                  {segment.equipment.name || segment.equipment.code}{" "}
                  {segment.marketingCarrier.flightNumber}
                </span>
              </div>

              <FlightInfo
                origin={{
                  date: segment.departure.date,
                  time: segment.departure.time,
                  code: segment.departure.airport.code,
                }}
                destination={{
                  date: segment.arrival.date,
                  time: segment.arrival.time,
                  code: segment.arrival.airport.code,
                }}
              >
                <FlightArrow overallTime={segment.duration} />
              </FlightInfo>

              {returnStops.length > 0 && returnStops[index]?.stopTime && (
                <Chip
                  icon={<AlarmIcon size={16} />}
                  color="primary"
                  label={t("flights.flightStopWithTimeAndCity", {
                    ...parseDuration(returnStops[index].stopTime),
                    cityName: segment.arrival.city.name,
                    code: segment.arrival.airport.code,
                  })}
                />
              )}
            </div>
          ))}
        <Divider sx={{ margin: "1.5rem 0" }} />
        <TravellersListSummary selectedTravellers={selectedTravellers} />
        <BaggageAllowance baggageData={singleFlightDetails?.outboundBaggage} />

        {singleFlightDetails?.returnBaggage !== null ? (
          <BaggageAllowance baggageData={singleFlightDetails?.returnBaggage} />
        ) : null}
      </div>

      <div>
        <div className="section-title">{t("reservations.whatsNext")}</div>
        <div className="section-card">
          <div className="flex flex-col gap-2">
            <h5 className="text-sm font-semibold text-text-primary">
              {t("flights.nextSteps")}
            </h5>
            <span className="block text-sm font-normal text-text-primary">
              {t("flights.nextStepsDescription")}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-auto mb-40 flex flex-col items-center gap-2">
        <div className="mt-6 w-60">
          <Button
            fullWidth
            onClick={() => {
              navigate({ to: "/" });
            }}
            variant="contained"
            size="small"
          >
            {t("backToHome")}
          </Button>
        </div>
        <div className="w-60">
          <Button
            fullWidth
            onClick={() => {
              navigate({ to: "/bookings", search: { category: "flights" } });
            }}
            variant="text"
            size="small"
          >
            {t("openMyBookings")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default FlightBookSuccess;
